import { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { calculateTradeMetrics, calculatePnL, convertFileToBase64 } from '../utils/tradeUtils';
import { useSettings } from '../contexts/SettingsContext';

const getDefaultDateTime = () => {
  const now = new Date();
  return {
    date: now.toISOString().split('T')[0],
    time: now.toTimeString().split(' ')[0].slice(0, 5)
  };
};

export const useImageAnalysis = (refreshSettingsCallback) => {
  const { settings, refreshSettings } = useSettings();
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [screenshotBase64, setScreenshotBase64] = useState('');
  const [loadingModel, setLoadingModel] = useState(false);
  
  const defaultDateTime = useMemo(() => getDefaultDateTime(), []);

  const [tradeData, setTradeData] = useState(() => ({
    category: 'stock', symbol: '', tradeDate: defaultDateTime.date, tradeTime: defaultDateTime.time,
    closeDate: '', closeTime: '', entryPrice: '', exitPrice: '', stopLoss: '', direction: 'LONG',
    riskRewardRatio: '', setup: '', insight: '', notes: '', lesson: '',
    positionSize: settings?.defaultPositionSize || '', leverage: 1, strategyIds: [], status: 'OPEN',
    tradingUrl: '', tags: settings?.defaultTags || [], rating: 3, exchange: ''
  }));

  const handleFileProcess = useCallback(async (file) => {
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    const base64 = await convertFileToBase64(file);
    setScreenshotBase64(base64);
  }, []);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files[0];
    if (file) handleFileProcess(file);
  }, [handleFileProcess]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileProcess(file);
  }, [handleFileProcess]);

  const analyzeImage = useCallback(async () => {
    if (!imageFile) return;
    setLoadingModel(true);
    try {
      const token = localStorage.getItem('idToken');
      const res = await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/imagetojson_openai', {
        image_base64: screenshotBase64,
        incrementQuota: true // Signal to increment AI quota usage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.data.extractedTradeData || {};
      if (!data.direction) data.direction = 'LONG';
      if (!data.tradeDate) data.tradeDate = defaultDateTime.date;
      if (!data.tradeTime) data.tradeTime = defaultDateTime.time;
      setTradeData(prev => ({ ...prev, ...data }));
      
      // Refresh settings to get updated quota information
      await refreshSettings();
      
      return { success: true, message: 'Analyzed with OpenAI' };
    } catch (error) {
      console.error('Analyze error:', error);
      return { success: false, message: 'Analysis failed.' };
    } finally {
      setLoadingModel(false);
    }
  }, [imageFile, screenshotBase64, defaultDateTime, refreshSettings]);

  const metrics = useMemo(() => {
    return calculateTradeMetrics(
      tradeData.entryPrice,
      tradeData.stopLoss,
      tradeData.exitPrice,
      tradeData.positionSize,
      tradeData.leverage
    );
  }, [tradeData.entryPrice, tradeData.stopLoss, tradeData.exitPrice, tradeData.positionSize, tradeData.leverage]);

  const createTrade = useCallback(async () => {
    // Check trade quota limits
    const tradeQuota = settings?.subscription?.tradeQuota;
    if (tradeQuota && tradeQuota.used >= tradeQuota.totalLimit) {
      return { 
        success: false, 
        message: `Trade limit reached (${tradeQuota.used}/${tradeQuota.totalLimit}). Upgrade your plan for more.`
      };
    }
    
    const payload = {
      ...tradeData,
      leverage: String(tradeData.leverage),
      positionSize: String(tradeData.positionSize),
      riskRewardRatio: String(metrics.riskRewardRatio || ''),
      quantity: metrics.quantity,
      rrSummary: `R:R = ${metrics.riskRewardRatioFormatted || '1:1'} | Risk: $${metrics.riskAmount} | Reward: $${metrics.rewardAmount} | Quantity: ${metrics.quantity}`,
      riskAmount: metrics.riskAmount,
      rewardAmount: metrics.rewardAmount,
      tradeDate: tradeData.tradeDate || defaultDateTime.date,
      tradeTime: tradeData.tradeTime || defaultDateTime.time,
      closeDate: tradeData.closeDate || '',
      closeTime: tradeData.closeTime || '',
      entryTime: defaultDateTime.time,
      profitLoss: calculatePnL(tradeData.status, metrics),
      pnl: calculatePnL(tradeData.status, metrics),
      screenshot: screenshotBase64,
      incrementQuota: true // Signal to increment trade quota usage
    };

    try {
      const token = localStorage.getItem('idToken');
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/trades', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh settings to get updated quota information
      await refreshSettings();
      
      return { success: true, message: 'Trade saved' };
    } catch (err) {
      console.error('Save error:', err);
      return { success: false, message: 'Failed to save trade' };
    }
  }, [tradeData, metrics, screenshotBase64, defaultDateTime, settings?.subscription?.tradeQuota, refreshSettings]);

  const clearImage = useCallback(() => {
    setImageFile(null);
    setPreviewUrl('');
    setScreenshotBase64('');
  }, []);

  return {
    imageFile,
    previewUrl,
    tradeData,
    setTradeData,
    loadingModel,
    metrics,
    handleFileSelect,
    handleDrop,
    analyzeImage,
    createTrade,
    clearImage
  };
};